import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Slugs considered "unpaid" tiers — trial, free, and anonymous users.
 * Centralised here so all pages use the same definition.
 */
const UNPAID_TIERS = new Set(['trial', 'free', 'anonymous', '']);

/**
 * Return true when the subscription tier represents a paying customer.
 *
 * @param tier - raw tier slug from the API (e.g. 'pro', 'trial', undefined)
 */
export function isPaidTier(tier: string | undefined): boolean {
  return !!tier && !UNPAID_TIERS.has(tier.toLowerCase());
}

/**
 * True when the tier is a free trial. Every account starts on a per-user trial,
 * so the trial UI (countdown, free-access copy) shows whenever tier === 'trial'.
 */
export function isPromotionalTrialTier(tier: string | undefined): boolean {
  return (tier || '').toLowerCase() === 'trial';
}

/**
 * Human-readable plan name for display.
 *
 * Priority:
 *   1. `tierName` from the API (e.g. "Starter", "Pro", "Team") — use as-is.
 *   2. Well-known tier slug → friendly label.
 *   3. Capitalise the raw slug as a last resort.
 *
 * @param tier     - raw slug returned by the API (e.g. 'pro')
 * @param tierName - optional display name from the API (e.g. 'Pro')
 */
export function formatTierName(tier: string | undefined, tierName?: string): string {
  // During promotional period with a trial tier, show the plan name + "(Promotional)".
  // Use the API-provided tierName when it carries a real plan name (e.g. "Personal", "Pro").
  // Fall back to "Free" only when tierName is absent or is a generic label like "Trial"/"Subscriber".
  if (isPromotionalTrialTier(tier)) {
    const trimmed = tierName?.trim();
    const GENERIC_NAMES = new Set(['Trial', 'Subscriber', 'Free Trial', '']);
    const display = trimmed && !GENERIC_NAMES.has(trimmed) ? trimmed : 'Free';
    return `${display} (Promotional)`;
  }
  if (tierName && tierName.trim()) return tierName.trim();
  if (!tier) return 'Free Trial';
  const slug = tier.toLowerCase();
  const LABELS: Record<string, string> = {
    trial: 'Free Trial',
    free: 'Free',
    anonymous: 'Free',
    starter: 'Starter',
    pro: 'Pro',
    team: 'Team',
    enterprise: 'Enterprise',
  };
  return LABELS[slug] ?? tier.charAt(0).toUpperCase() + tier.slice(1).toLowerCase();
}
